/**
 * ex-show-variants-reveal-fix.js
 *
 * Fixes a compatibility issue between the "EX Show Variants on Collection" app
 * and Prestige theme's built-in reveal-on-scroll animation (see js/common/product/product-list.js,
 * bundled inside theme.js).
 *
 * Background:
 * Prestige's <product-list> element runs its reveal animation exactly once per
 * element, the first time it enters the viewport. It only animates the
 * product-card[reveal-on-scroll="true"] elements that exist in the DOM at that
 * exact moment. Cards that the "EX Show Variants" app injects afterwards share
 * the same reveal-on-scroll="true" attribute and the same default
 * `opacity: 0` CSS rule, but are never picked up by that one-time animation,
 * so they stay invisible forever.
 *
 * This script does NOT touch theme.js / theme.css / product-card.liquid, and
 * does NOT run the theme's reveal animation itself. It only detects cards that
 * are stuck at `opacity: 0` well after they entered the viewport (i.e. after
 * Prestige's own animation had every chance to run) and restores them to
 * their resting state.
 *
 * 2026-08 update:
 * Cards found during the initial DOM scan (i.e. already present when this
 * script starts, which includes Prestige's own cards) keep a generous grace
 * period so we never race Prestige's native reveal animation. Cards that are
 * detected afterwards via MutationObserver (i.e. inserted dynamically -
 * typically by EX Show Variants, but also by facet re-renders, etc.) are, by
 * definition, cards Prestige's one-time reveal() call can never reach again,
 * so they get a much shorter grace period to reduce the perceived delay.
 * See the performance investigation notes shared with the client for the
 * measurements behind this change.
 */
(function () {
  "use strict";

  // Guard against the script being included/executed more than once.
  if (window.__exShowVariantsRevealFixLoaded) {
    return;
  }
  window.__exShowVariantsRevealFixLoaded = true;

  var REVEAL_SELECTOR = 'product-card[reveal-on-scroll="true"]';

  // Grace period for cards present during the initial scan (Prestige's own
  // cards, plus anything already in the DOM before this script ran). Kept
  // generous (duration 0.2s + stagger, can add up for large grids) so we
  // never interfere with Prestige's native fade/translate-in animation.
  var INITIAL_GRACE_DELAY_MS = 1800;

  // Grace period for cards detected via MutationObserver, i.e. inserted into
  // the DOM after this script's initial scan already ran. Prestige's
  // product-list reveal() only ever fires once, early in the page life, so
  // it structurally cannot animate these cards - there is no native
  // animation to race against. A short buffer is kept (rather than 0ms) in
  // case the app that inserted the card is still finishing writing its
  // image/title/price into the card right after insertion.
  var DYNAMIC_GRACE_DELAY_MS = 150;

  // Per-card bookkeeping. Using WeakMap/WeakSet so entries are garbage
  // collected automatically once a card is removed from the DOM (e.g. by a
  // facet re-render), and so we never leak memory across repeated
  // filtering/sorting/pagination cycles.
  var hasWeakCollections = typeof WeakMap !== "undefined" && typeof WeakSet !== "undefined";

  // Maps a tracked card -> its grace delay in ms (also doubles as the
  // "already tracked" registry, preventing duplicate IntersectionObserver
  // registrations for the same node).
  var cardGraceDelay = hasWeakCollections ? new WeakMap() : null;

  // Cards we've already corrected (prevents re-applying the fix / re-running
  // the check needlessly on subsequent mutations).
  var fixedCards = hasWeakCollections ? new WeakSet() : null;

  var intersectionObserver = null;

  function isTracked(card) {
    return cardGraceDelay ? cardGraceDelay.has(card) : card.hasAttribute("data-ex-reveal-fix-tracked");
  }

  function markTracked(card, delay) {
    if (cardGraceDelay) {
      cardGraceDelay.set(card, delay);
    } else {
      card.setAttribute("data-ex-reveal-fix-tracked", "true");
      card.setAttribute("data-ex-reveal-fix-delay", String(delay));
    }
  }

  function getGraceDelay(card) {
    if (cardGraceDelay) {
      return cardGraceDelay.has(card) ? cardGraceDelay.get(card) : INITIAL_GRACE_DELAY_MS;
    }
    var attr = card.getAttribute("data-ex-reveal-fix-delay");
    return attr ? parseInt(attr, 10) : INITIAL_GRACE_DELAY_MS;
  }

  function isFixed(card) {
    return fixedCards ? fixedCards.has(card) : card.hasAttribute("data-ex-reveal-fix-applied");
  }

  function markFixed(card) {
    if (fixedCards) {
      fixedCards.add(card);
    } else {
      card.setAttribute("data-ex-reveal-fix-applied", "true");
    }
  }

  function getIntersectionObserver() {
    if (intersectionObserver || typeof IntersectionObserver === "undefined") {
      return intersectionObserver;
    }

    intersectionObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }
        var card = entry.target;
        // One-shot: we only need to know the card entered the viewport once.
        // Unobserving releases the watch as soon as it's no longer needed.
        observer.unobserve(card);
        scheduleOpacityCheck(card);
      });
    }, { threshold: 0 });

    return intersectionObserver;
  }

  function scheduleOpacityCheck(card) {
    var delay = getGraceDelay(card);
    window.setTimeout(function () {
      fixCardIfStuck(card);
    }, delay);
  }

  function fixCardIfStuck(card) {
    if (!card || isFixed(card)) {
      return;
    }
    // Card may have been removed from the DOM in the meantime (e.g. a facet
    // re-render replaced the whole grid) - nothing to do in that case.
    if (!card.isConnected) {
      return;
    }
    if (card.getAttribute("reveal-on-scroll") !== "true") {
      return;
    }

    var computedOpacity;
    try {
      computedOpacity = window.getComputedStyle(card).opacity;
    } catch (e) {
      return;
    }

    // Only touch cards that are still stuck at opacity:0. Cards Prestige (or
    // an earlier pass of this script) already revealed are left untouched.
    if (computedOpacity === "0") {
      card.style.opacity = "1";
      card.style.transform = "none";
      markFixed(card);
    }
  }

  function trackCard(card, isDynamic) {
    if (!card || isTracked(card)) {
      return;
    }
    if (card.getAttribute("reveal-on-scroll") !== "true") {
      return;
    }
    markTracked(card, isDynamic ? DYNAMIC_GRACE_DELAY_MS : INITIAL_GRACE_DELAY_MS);

    var observer = getIntersectionObserver();
    if (observer) {
      observer.observe(card);
    } else {
      // No IntersectionObserver support: fall back to a single delayed check.
      scheduleOpacityCheck(card);
    }
  }

  function scanNode(node, isDynamic) {
    if (!node || node.nodeType !== 1) {
      return;
    }
    if (typeof node.matches === "function" && node.matches(REVEAL_SELECTOR)) {
      trackCard(node, isDynamic);
    }
    if (typeof node.querySelectorAll === "function") {
      node.querySelectorAll(REVEAL_SELECTOR).forEach(function (card) {
        trackCard(card, isDynamic);
      });
    }
  }

  function initialScan() {
    // Everything present at this point is treated as an "initial" card
    // (Prestige's own cards, and anything else already in the DOM before
    // this script ran) and keeps the long, Prestige-safe grace period.
    document.querySelectorAll(REVEAL_SELECTOR).forEach(function (card) {
      trackCard(card, false);
    });
  }

  function startMutationObserver() {
    if (typeof MutationObserver === "undefined" || !document.body) {
      return;
    }
    // A single, persistent observer on <body> survives facet filtering /
    // sorting / pagination re-renders (which replace the <product-list>
    // element entirely), so we don't need to re-attach it per section. Every
    // node this observer discovers is, by definition, added after the
    // initial scan, so it's treated as "dynamic" and gets the short delay.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          scanNode(node, true);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    initialScan();
    startMutationObserver();
  }

  if (document.body) {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  }
})();
